import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { usePlacesWidget } from 'react-google-autocomplete';
import styles from './styles.module.scss';
import config from '../../../../config';

/**
 * AddressAutocomplete component for V2 UI
 * Provides Google Places API integration for address fields
 * 
 * @param {Object} props - Component props
 * @param {string} props.id - Input ID
 * @param {string} props.name - Input name for form submission
 * @param {string} props.label - Input label text
 * @param {string} props.placeholder - Input placeholder text
 * @param {function} props.onPlaceSelected - Callback when a place is selected from dropdown
 * @param {React.ReactNode} props.prefix - Optional prefix element (icon)
 * @param {React.ReactNode} props.suffix - Optional suffix element (icon)
 * @param {boolean} props.touched - Whether the field has been touched (for validation)
 * @param {boolean} props.errored - Whether the field has an error (for validation)
 * @param {string} props.error - Error message to display
 * @param {string} props.countryRestriction - Optional country restriction (default: ["us", "ca"])
 */
const AddressAutocomplete = ({
  id,
  name,
  label,
  placeholder,
  onPlaceSelected,
  prefix,
  suffix,
  touched,
  errored,
  error,
  countryRestriction,
  value,
  ...otherProps
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef(null);
  const [localValue, setLocalValue] = useState(value || '');
  const apiKey = config.paymentcloud.GOOGLE_API_KEY;
  
  // Ensure errored is treated as boolean
  const hasError = errored ? true : false;
  
  // Check if Google Maps is already available globally
  const [isReady, setIsReady] = useState(false);

  const inputClassNames = [
    styles.input,
    prefix ? styles.prefixInput : '',
    suffix ? styles.suffixInput : '',
    touched && hasError ? styles.errorInput : '',
  ].filter(Boolean).join(' ');

  // Update local value when prop value changes
  useEffect(() => {
    if (value !== undefined) {
      setLocalValue(value);
    }
  }, [value]);

  useEffect(() => {
    
    if (!apiKey) {
      return;
    }

    // Check if script is already loaded
    if (window.google && window.google.maps) {
      setIsReady(true);
      return;
    }

    // Check if there's already a script loading
    const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
    
    if (existingScript) {
      // If the script is still loading, wait for it
      const handleExistingScriptLoad = () => {
        setIsReady(true);
      };
      
      existingScript.addEventListener('load', handleExistingScriptLoad);
      return () => {
        existingScript.removeEventListener('load', handleExistingScriptLoad);
      };
    }
    
    // Otherwise, load the script directly
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.onload = () => {
      setIsReady(true);
    };
    script.onerror = (error) => {
      console.error("Error loading Google Maps script:", error);
    };
    
    document.head.appendChild(script);
    
    return () => {
      // Cleanup function
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [apiKey]);

  // Only use the places widget when Google Maps is ready
  const { ref: placesRef } = usePlacesWidget({
    apiKey, // Pass the API key directly
    onPlaceSelected: (place) => {
      if (onPlaceSelected && place) {
        onPlaceSelected(place);
      }
    },
    options: {
      types: ['address'],
      componentRestrictions: countryRestriction 
        ? { country: countryRestriction } 
        : { country: 'us' }  // Default to US only if not specified
    },
    inputAutocompleteValue: 'new-address',
    disabled: !isReady
  });

  useEffect(() => {
    // Apply custom styles to the Google Places Autocomplete dropdown
    if (isReady && containerRef.current && isFocused) {
      // This ensures the dropdown stays styled even when it's detached from the DOM
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.addedNodes.length) {
            mutation.addedNodes.forEach((node) => {
              if (node.classList && node.classList.contains('pac-container')) {
                node.classList.add(styles.pacContainer);
              }
            });
          }
        });
      });

      observer.observe(document.body, { childList: true });

      return () => {
        observer.disconnect();
      };
    }
  }, [isReady, isFocused]);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };
  
  const handleChange = (e) => {
    setLocalValue(e.target.value);
    if (otherProps.onChange) {
      otherProps.onChange(e);
    }
  };

  return (
    <div className={styles.formGroup} ref={containerRef}>
      {label && <label htmlFor={id} className={styles.label}>{label}</label>}
      <div className={styles.inputWrapper}>
        {prefix && <div className={styles.prefix}>{prefix}</div>}
        <input
          id={id}
          name={name}
          ref={placesRef}
          className={inputClassNames}
          placeholder={placeholder}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={!isReady}
          value={localValue}
          onChange={handleChange}
          {...otherProps}
        />
        {suffix && <div className={styles.suffix}>{suffix}</div>}
      </div>
      
      {!isReady && (
        <span className={styles.error}>
          Loading Google Maps API...
        </span>
      )}
      
      {touched && hasError && error && (
        <span className={styles.error}>{error}</span>
      )}
    </div>
  );
};

AddressAutocomplete.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  placeholder: PropTypes.string,
  onPlaceSelected: PropTypes.func,
  prefix: PropTypes.node,
  suffix: PropTypes.node,
  touched: PropTypes.bool,
  errored: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  error: PropTypes.string,
  value: PropTypes.string,
  countryRestriction: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)])
};

AddressAutocomplete.defaultProps = {
  label: '',
  placeholder: 'Enter an address',
  touched: false,
  errored: false,
  error: '',
  value: '',
  countryRestriction: ''
};

export default AddressAutocomplete; 